// SS3 Biology — Complete NERDC Curriculum Quiz Data
// First Term only (Second Term and Third Term not present in NERDC scheme for SS3 Biology)
// 8 topics x 20 questions = 160 questions

export const WAEC_BIOLOGY_LEARN_SS3 = [
  {
    "topic": "Reproductive Behaviours in Animals",
    "topicCode": "SS3-BIO-01",
    "module": "Reproduction and Heredity",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Reproductive behaviour</span> refers to the set of actions animals perform to attract mates, secure fertilization, and raise offspring successfully. These behaviours have evolved because they increase the chances of a species' survival.\n</div>\n\n<h3 class=\"learn-subheading\">1. Courtship Behaviour</h3>\n<p class=\"learn-p\"><span class=\"learn-keyword\">Courtship</span> is the set of behaviours an animal displays to attract a mate and signal readiness to reproduce — for example, birds singing or displaying colourful plumage, or peacocks fanning their tail feathers. Courtship helps individuals select healthy, suitable mates and reduces the chance of interbreeding between different species.</p>\n\n<h3 class=\"learn-subheading\">2. Mating Systems</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>System</th><th>Description</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Monogamy</td><td>One male mates with one female, often for a season or life</td><td>Many bird species, e.g., swans</td></tr>\n      <tr><td>Polygamy</td><td>One individual mates with multiple partners of the opposite sex</td><td>Lions (polygyny), some insects</td></tr>\n      <tr><td>Promiscuity</td><td>Both males and females mate with multiple partners with no lasting bond</td><td>Chimpanzees, many fish species</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Parental Care</h3>\n<p class=\"learn-p\"><span class=\"learn-keyword\">Parental care</span> is the investment parents make in protecting and nurturing offspring after fertilization. It varies greatly across animal groups:</p>\n<ul class=\"learn-list\">\n  <li><strong>Fishes:</strong> Many show little or no parental care (external fertilization, large numbers of eggs); some species like tilapia mouth-brood their young</li>\n  <li><strong>Amphibians:</strong> Some frogs guard eggs or carry tadpoles; most rely on producing many eggs instead</li>\n  <li><strong>Birds:</strong> High parental care — incubating eggs, feeding, and protecting chicks until they can fly</li>\n  <li><strong>Mammals:</strong> Highest parental care — internal development, nursing with milk, and prolonged protection</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Importance of Reproductive Behaviours</h3>\n<p class=\"learn-p\">These behaviours increase the survival chances of offspring, ensure successful fertilization, maintain species identity by preventing cross-breeding, and help pass on favourable genetic traits to the next generation.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Don't confuse \"polygamy\" (one individual with multiple mates) with \"promiscuity\" (both sexes mating with multiple partners with no fixed bond) — WAEC often tests this distinction directly.\n</div>\n",
    "questions": [
      {
        "q": "Courtship behaviour in animals primarily serves to:",
        "o": [
          "Attract a mate and signal readiness to reproduce",
          "Help animals find food",
          "Protect territory from predators only",
          "Regulate body temperature"
        ],
        "a": 0,
        "e": "Courtship behaviour is displayed by animals to attract a mate and indicate readiness to reproduce, such as birds singing or displaying plumage.",
        "h": "Think about the purpose of a peacock fanning its tail.",
        "yr": "SS3"
      },
      {
        "q": "In a mating system where one male mates with one female for a season or life, the system is called:",
        "o": [
          "Polygamy",
          "Promiscuity",
          "Monogamy",
          "Parthenogenesis"
        ],
        "a": 2,
        "e": "Monogamy describes a mating system where one male pairs with one female, often for an extended period or for life, as seen in swans.",
        "h": "Think of the prefix 'mono-' meaning 'one'.",
        "yr": "SS3"
      },
      {
        "q": "A mating system where one individual (e.g., a male lion) mates with multiple partners is called:",
        "o": [
          "Monogamy",
          "Polygamy",
          "Parental care",
          "Courtship"
        ],
        "a": 1,
        "e": "Polygamy describes a mating system where one individual mates with multiple partners of the opposite sex, as seen in lions (polygyny).",
        "h": "Think of the prefix 'poly-' meaning 'many'.",
        "yr": "SS3"
      },
      {
        "q": "Promiscuity as a mating system is best described as:",
        "o": [
          "A single lifelong pairing",
          "Both males and females mating with multiple partners with no lasting bond",
          "Only females mating with one partner",
          "A behaviour unique to birds"
        ],
        "a": 1,
        "e": "Promiscuity involves both males and females mating with multiple partners without forming a lasting pair bond, as seen in chimpanzees.",
        "h": "Recall the definition distinguishing this from polygamy.",
        "yr": "SS3"
      },
      {
        "q": "Which group of animals generally shows the HIGHEST level of parental care?",
        "o": [
          "Fishes",
          "Amphibians",
          "Birds",
          "Mammals"
        ],
        "a": 3,
        "e": "Mammals generally show the highest level of parental care, including internal development, nursing with milk, and prolonged protection of young.",
        "h": "Think about which group nurses its young with milk.",
        "yr": "SS3"
      },
      {
        "q": "Many fish species show little parental care mainly because they:",
        "o": [
          "Produce very few eggs that require intense protection",
          "Rely on external fertilization and producing large numbers of eggs instead",
          "Cannot reproduce sexually at all",
          "Always live in complete isolation"
        ],
        "a": 1,
        "e": "Many fish species rely on external fertilization and producing very large numbers of eggs as a reproductive strategy, rather than intensive parental care.",
        "h": "Think about the strategy of quantity over individual protection.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an example of parental care in birds?",
        "o": [
          "Incubating eggs and feeding chicks until they can fly",
          "Releasing eggs into water with no further involvement",
          "Never building any form of nest",
          "Abandoning eggs immediately after laying"
        ],
        "a": 0,
        "e": "Birds typically show high parental care by incubating their eggs and feeding and protecting chicks until they are capable of flying.",
        "h": "Think of typical bird nesting behaviour.",
        "yr": "SS3"
      },
      {
        "q": "Reproductive behaviours are important mainly because they:",
        "o": [
          "Have no real effect on species survival",
          "Increase survival chances of offspring and help pass on favourable traits",
          "Only matter for animals in captivity",
          "Prevent all animals from reproducing"
        ],
        "a": 1,
        "e": "Reproductive behaviours increase the survival chances of offspring, ensure successful fertilization, and help pass on favourable genetic traits.",
        "h": "Think of the evolutionary advantage of these behaviours.",
        "yr": "SS3"
      },
      {
        "q": "Courtship displays, such as colourful plumage, help animals to:",
        "o": [
          "Select healthy, suitable mates and reduce interbreeding between species",
          "Avoid reproduction altogether",
          "Confuse predators only",
          "Change their body temperature"
        ],
        "a": 0,
        "e": "Courtship displays help animals select healthy, suitable mates of their own species, reducing the risk of interbreeding between different species.",
        "h": "Think about mate selection and species identity.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following best distinguishes polygamy from promiscuity?",
        "o": [
          "Polygamy involves one individual with multiple mates; promiscuity involves both sexes mating with multiple partners with no fixed bond",
          "They are exactly the same mating system",
          "Promiscuity always involves a single lifelong partner",
          "Polygamy only occurs in fish"
        ],
        "a": 0,
        "e": "Polygamy involves one individual (of either sex) mating with multiple partners, while promiscuity involves both males and females mating with multiple partners without a lasting bond.",
        "h": "Recall the exam tip distinguishing these two systems.",
        "yr": "SS3"
      },
      {
        "q": "Tilapia fish mouth-brooding their young is an example of:",
        "o": [
          "A complete absence of parental care",
          "Parental care in a fish species",
          "A form of courtship only",
          "Human evolution"
        ],
        "a": 1,
        "e": "Mouth-brooding, where a fish carries its young in its mouth for protection, is an example of parental care shown by some fish species like tilapia.",
        "h": "Think about what mouth-brooding achieves for the young.",
        "yr": "SS3"
      },
      {
        "q": "Some frogs guarding their eggs or carrying tadpoles demonstrates:",
        "o": [
          "Parental care in amphibians",
          "A total lack of reproductive behaviour",
          "Polygamy specifically",
          "Human evolution"
        ],
        "a": 0,
        "e": "Some frog species guard their eggs or carry tadpoles, showing a degree of parental care within amphibians, though most rely more on producing many eggs.",
        "h": "Think about protective behaviours in some amphibian species.",
        "yr": "SS3"
      },
      {
        "q": "Internal development and nursing with milk are features most characteristic of parental care in:",
        "o": [
          "Fishes",
          "Amphibians",
          "Birds",
          "Mammals"
        ],
        "a": 3,
        "e": "Internal development of the embryo and nursing offspring with milk are defining features of mammalian parental care.",
        "h": "Think of the defining trait of mammals — the word 'mammal' relates to milk glands.",
        "yr": "SS3"
      },
      {
        "q": "Which mating system is exemplified by many species of birds like swans, which often pair for life?",
        "o": [
          "Monogamy",
          "Promiscuity",
          "Polygamy",
          "Random mating"
        ],
        "a": 0,
        "e": "Swans are a classic example of monogamy, where a pair bonds and mates together, often for life.",
        "h": "Recall the example given for monogamy.",
        "yr": "SS3"
      },
      {
        "q": "The primary evolutionary advantage of reproductive behaviours like courtship and parental care is that they:",
        "o": [
          "Have no measurable effect on a species",
          "Increase the likelihood of successful reproduction and offspring survival",
          "Only benefit predators of the species",
          "Reduce genetic diversity to zero"
        ],
        "a": 1,
        "e": "These behaviours evolved because they increase the likelihood of successful reproduction and improve the survival chances of offspring.",
        "h": "Think about why evolution would favour such behaviours.",
        "yr": "SS3"
      },
      {
        "q": "Which statement about chimpanzees' mating behaviour is most accurate, based on the lesson?",
        "o": [
          "Chimpanzees are strictly monogamous",
          "Chimpanzees exhibit promiscuity, with both sexes mating with multiple partners",
          "Chimpanzees show no reproductive behaviour at all",
          "Chimpanzees mate only once in their lifetime"
        ],
        "a": 1,
        "e": "Chimpanzees are given as an example of promiscuity, where both males and females mate with multiple partners without a lasting bond.",
        "h": "Recall the example given for promiscuity.",
        "yr": "SS3"
      },
      {
        "q": "Why do amphibians, unlike mammals, typically produce a much larger number of eggs?",
        "o": [
          "Because they invest less in direct parental care and rely more on offspring quantity for survival",
          "Because amphibians cannot reproduce sexually",
          "Because mammals produce even more eggs than amphibians",
          "There is no difference between the two groups"
        ],
        "a": 0,
        "e": "Amphibians generally invest less in direct parental care compared to mammals, relying instead on producing large numbers of eggs to improve overall survival odds.",
        "h": "Think about the trade-off between egg quantity and parental investment.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would be considered part of an animal's courtship behaviour?",
        "o": [
          "A bird building a nest for migration only",
          "A male bird singing and displaying bright plumage to attract a female",
          "An animal hibernating during winter",
          "An animal shedding its skin"
        ],
        "a": 1,
        "e": "A male bird singing and displaying bright plumage is a classic example of courtship behaviour used to attract a mate.",
        "h": "Look for the behaviour specifically aimed at attracting a mate.",
        "yr": "SS3"
      },
      {
        "q": "Preventing interbreeding between different species is a benefit most directly linked to:",
        "o": [
          "Random mutation",
          "Courtship behaviour, which helps animals identify suitable mates of their own species",
          "Photosynthesis",
          "Human evolution"
        ],
        "a": 1,
        "e": "Courtship behaviour helps animals identify and select mates of their own species, thereby helping to prevent interbreeding between different species.",
        "h": "Recall the benefit of courtship discussed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best summarises the overall importance of studying reproductive behaviours in animals?",
        "o": [
          "It has no practical or scientific value",
          "It helps explain how species maximise reproductive success and ensure survival across generations",
          "It is only relevant to studying plants",
          "It replaces the need to study genetics"
        ],
        "a": 1,
        "e": "Studying reproductive behaviours helps explain how species maximise reproductive success and ensure their survival across generations, connecting closely to genetics and evolution.",
        "h": "Think of the broader purpose of this topic within biology.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Biology of Heredity (Genetics I)",
    "topicCode": "SS3-BIO-02",
    "module": "Reproduction and Heredity",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Heredity</span> is the passing of characteristics (traits) from parents to offspring. <span class=\"learn-keyword\">Genetics</span> is the branch of biology that studies how these traits are inherited, largely founded on the pioneering work of Gregor Mendel.\n</div>\n\n<h3 class=\"learn-subheading\">1. Gregor Mendel and the History of Genetics</h3>\n<p class=\"learn-p\">Gregor Mendel, an Austrian monk, conducted breeding experiments on pea plants in the 1860s, carefully tracking traits like seed shape and flower colour across generations. His work established the basic rules of inheritance, though it was not widely recognised until decades later.</p>\n\n<h3 class=\"learn-subheading\">2. Key Genetics Terminology</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Meaning</th></tr></thead>\n    <tbody>\n      <tr><td>Gene</td><td>A unit of heredity that codes for a particular characteristic, located on a chromosome</td></tr>\n      <tr><td>Allele</td><td>An alternative form of a gene (e.g., one allele for tall, one for short)</td></tr>\n      <tr><td>Chromosome</td><td>A thread-like structure in the nucleus made of DNA that carries genes</td></tr>\n      <tr><td>Genotype</td><td>The genetic makeup of an organism (its combination of alleles, e.g., Tt)</td></tr>\n      <tr><td>Phenotype</td><td>The observable physical characteristics resulting from the genotype (e.g., tall plant)</td></tr>\n      <tr><td>Homozygous</td><td>Having two identical alleles for a trait (e.g., TT or tt)</td></tr>\n      <tr><td>Heterozygous</td><td>Having two different alleles for a trait (e.g., Tt)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Mendel's Laws of Inheritance</h3>\n<ul class=\"learn-list\">\n  <li><strong>Law of Segregation:</strong> Each organism carries two alleles for a trait, which separate during gamete formation so each gamete carries only one allele</li>\n  <li><strong>Law of Independent Assortment:</strong> Alleles of different genes are distributed to gametes independently of one another (for genes on different chromosomes)</li>\n  <li><strong>Law of Dominance:</strong> When two different alleles are present (heterozygous), one (the dominant allele) masks the expression of the other (the recessive allele)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Example: Flower Colour in Pea Plants</h3>\n<p class=\"learn-p\">If purple flower colour (P) is dominant over white (p), a plant with genotype Pp will have a purple phenotype, since the dominant P allele masks the recessive p allele. Only pp plants show the white phenotype.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember: genotype is the genetic code (letters like Tt), while phenotype is what you actually observe (e.g., \"tall plant\"). Many WAEC questions test whether you can correctly link a genotype to its resulting phenotype.\n</div>\n",
    "questions": [
      {
        "q": "Heredity is best defined as:",
        "o": [
          "The study of animal behaviour",
          "The passing of characteristics from parents to offspring",
          "The classification of living things",
          "The process of photosynthesis"
        ],
        "a": 1,
        "e": "Heredity refers to the passing of characteristics (traits) from parents to their offspring through genes.",
        "h": "Think of traits passed down across generations.",
        "yr": "SS3"
      },
      {
        "q": "Gregor Mendel is best known for conducting breeding experiments on:",
        "o": [
          "Fruit flies",
          "Pea plants",
          "Guinea pigs",
          "Bacteria"
        ],
        "a": 1,
        "e": "Gregor Mendel conducted his foundational genetics experiments on pea plants in the 1860s, tracking traits across generations.",
        "h": "Recall the specific plant used in Mendel's famous experiments.",
        "yr": "SS3"
      },
      {
        "q": "A 'gene' is best described as:",
        "o": [
          "A unit of heredity that codes for a particular characteristic",
          "A type of cell membrane",
          "An organ found only in animals",
          "A form of asexual reproduction"
        ],
        "a": 0,
        "e": "A gene is a unit of heredity, located on a chromosome, that codes for a particular characteristic or trait.",
        "h": "Think of the basic hereditary unit.",
        "yr": "SS3"
      },
      {
        "q": "An 'allele' refers to:",
        "o": [
          "A whole chromosome",
          "An alternative form of a gene",
          "The physical appearance of an organism",
          "A type of cell division"
        ],
        "a": 1,
        "e": "An allele is an alternative form of a gene — for example, one allele might code for tall plants and another for short plants.",
        "h": "Think of the different 'versions' of a single gene.",
        "yr": "SS3"
      },
      {
        "q": "A 'chromosome' is best described as:",
        "o": [
          "A thread-like structure in the nucleus made of DNA that carries genes",
          "A type of enzyme",
          "A single allele",
          "An observable physical trait"
        ],
        "a": 0,
        "e": "A chromosome is a thread-like structure found in the nucleus, made of DNA, that carries genes.",
        "h": "Think of the structure that houses genes within the nucleus.",
        "yr": "SS3"
      },
      {
        "q": "'Genotype' refers to:",
        "o": [
          "The observable physical characteristics of an organism",
          "The genetic makeup of an organism (its combination of alleles)",
          "The organism's habitat",
          "The organism's behaviour only"
        ],
        "a": 1,
        "e": "Genotype refers specifically to the genetic makeup of an organism — its combination of alleles, such as Tt.",
        "h": "Think of the underlying genetic code, not what you can see.",
        "yr": "SS3"
      },
      {
        "q": "'Phenotype' refers to:",
        "o": [
          "The genetic code represented by letters like Tt",
          "The observable physical characteristics resulting from the genotype",
          "A type of chromosome",
          "An allele on its own"
        ],
        "a": 1,
        "e": "Phenotype refers to the observable physical characteristics that result from an organism's genotype, such as being a tall plant.",
        "h": "Think of what you can actually see or observe.",
        "yr": "SS3"
      },
      {
        "q": "An organism with two identical alleles for a trait (e.g., TT) is described as:",
        "o": [
          "Heterozygous",
          "Homozygous",
          "Recessive only",
          "Dominant only"
        ],
        "a": 1,
        "e": "An organism with two identical alleles for a trait, such as TT or tt, is described as homozygous.",
        "h": "Think of the prefix 'homo-' meaning 'same'.",
        "yr": "SS3"
      },
      {
        "q": "An organism with two different alleles for a trait (e.g., Tt) is described as:",
        "o": [
          "Homozygous",
          "Heterozygous",
          "Genotype-free",
          "Phenotype-free"
        ],
        "a": 1,
        "e": "An organism with two different alleles for a trait, such as Tt, is described as heterozygous.",
        "h": "Think of the prefix 'hetero-' meaning 'different'.",
        "yr": "SS3"
      },
      {
        "q": "According to Mendel's Law of Segregation:",
        "o": [
          "Alleles never separate during gamete formation",
          "The two alleles for a trait separate during gamete formation so each gamete carries only one allele",
          "All organisms carry only one allele for each trait",
          "Chromosomes are destroyed during reproduction"
        ],
        "a": 1,
        "e": "The Law of Segregation states that the two alleles for a trait separate during gamete formation, so each gamete receives only one allele.",
        "h": "Recall the first law listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Mendel's Law of Dominance states that:",
        "o": [
          "Recessive alleles always mask dominant alleles",
          "When two different alleles are present, the dominant allele masks the expression of the recessive allele",
          "All alleles have equal expression at all times",
          "Dominant alleles are always harmful"
        ],
        "a": 1,
        "e": "The Law of Dominance states that when two different alleles are present in a heterozygous organism, the dominant allele masks the expression of the recessive one.",
        "h": "Recall the third law listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "If purple flower colour (P) is dominant over white (p), a plant with genotype Pp will have which phenotype?",
        "o": [
          "White",
          "Purple",
          "A mix of both colours on the same petal, always",
          "No flower colour at all"
        ],
        "a": 1,
        "e": "Since P is dominant over p, a Pp plant will show the purple phenotype, as the dominant allele masks the recessive white allele.",
        "h": "Recall the worked example given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "In the same example, which genotype would result in a WHITE phenotype?",
        "o": [
          "PP",
          "Pp",
          "pp",
          "None of the above"
        ],
        "a": 2,
        "e": "Only the homozygous recessive genotype 'pp' would result in the white phenotype, since no dominant P allele is present to mask it.",
        "h": "Think about which genotype has no dominant allele present.",
        "yr": "SS3"
      },
      {
        "q": "According to Mendel's Law of Independent Assortment:",
        "o": [
          "Alleles of different genes are distributed to gametes independently of one another",
          "All genes must always be inherited together",
          "Only one gene can exist per organism",
          "Chromosomes cannot carry more than one gene"
        ],
        "a": 0,
        "e": "The Law of Independent Assortment states that alleles of different genes (on different chromosomes) are distributed to gametes independently of one another.",
        "h": "Recall the second law listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Why is Gregor Mendel considered foundational to the field of genetics?",
        "o": [
          "He discovered DNA structure directly",
          "His pea plant experiments established the basic rules of inheritance",
          "He invented the microscope",
          "He was the first to classify living things into kingdoms"
        ],
        "a": 1,
        "e": "Mendel's carefully documented pea plant experiments established the basic rules of inheritance, forming the foundation of modern genetics.",
        "h": "Recall his specific historical contribution described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which of these correctly matches genotype and phenotype for the term 'Tt' and its likely observable trait if T (tall) is dominant?",
        "o": [
          "Genotype 'Tt'; phenotype: tall plant",
          "Genotype 'Tt'; phenotype: short plant",
          "Genotype 'tall plant'; phenotype: 'Tt'",
          "There is no relationship between the two"
        ],
        "a": 0,
        "e": "The genotype 'Tt' is heterozygous, and since T (tall) is dominant, the resulting phenotype observed would be a tall plant.",
        "h": "Recall the exam tip about linking genotype to phenotype.",
        "yr": "SS3"
      },
      {
        "q": "Which statement correctly distinguishes a gene from a chromosome?",
        "o": [
          "A gene is a unit of heredity coding for a trait; a chromosome is the larger DNA structure that carries many genes",
          "A chromosome is smaller than a single gene",
          "They are exactly the same structure",
          "Genes exist outside the nucleus, while chromosomes exist inside it"
        ],
        "a": 0,
        "e": "A gene is a specific unit of heredity coding for a trait, while a chromosome is the larger thread-like DNA structure that carries many genes.",
        "h": "Think about the relationship in scale between the two terms.",
        "yr": "SS3"
      },
      {
        "q": "Understanding Mendel's laws helps explain:",
        "o": [
          "How traits are transmitted from parents to offspring in predictable patterns",
          "How plants perform photosynthesis",
          "How animals digest food",
          "How ecosystems recycle nutrients"
        ],
        "a": 0,
        "e": "Mendel's laws of inheritance explain how traits are transmitted from parents to offspring in predictable, mathematically describable patterns.",
        "h": "Focus on the overall purpose of studying these laws.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following pairs would be classified as homozygous genotypes?",
        "o": [
          "TT and tt",
          "Tt only",
          "TT and Tt",
          "None of the genotypes given"
        ],
        "a": 0,
        "e": "Both 'TT' and 'tt' are homozygous genotypes because each consists of two identical alleles.",
        "h": "Look for genotypes with matching letters (both capital or both lowercase).",
        "yr": "SS3"
      },
      {
        "q": "A trait controlled by a recessive allele will only be expressed in the phenotype when the organism is:",
        "o": [
          "Heterozygous for that trait",
          "Homozygous dominant for that trait",
          "Homozygous recessive for that trait",
          "Missing that gene entirely"
        ],
        "a": 2,
        "e": "A recessive trait is only expressed in the phenotype when the organism is homozygous recessive (e.g., pp), since a single dominant allele would mask it.",
        "h": "Think about when a recessive allele would have no dominant allele to mask it.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Genetics II",
    "topicCode": "SS3-BIO-03",
    "module": "Reproduction and Heredity",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Building on basic genetics terms, this topic covers <span class=\"learn-keyword\">monohybrid and dihybrid crosses</span>, the <span class=\"learn-keyword\">Punnett square</span> tool for predicting offspring, human sex determination, and sex-linked traits.\n</div>\n\n<h3 class=\"learn-subheading\">1. Monohybrid Cross</h3>\n<p class=\"learn-p\">A <span class=\"learn-keyword\">monohybrid cross</span> studies the inheritance of a single trait. Example: crossing a homozygous tall plant (TT) with a homozygous short plant (tt) produces all Tt (tall) offspring in the first generation (F1). Crossing two Tt plants (F1 x F1) produces a 3:1 ratio of tall to short in the F2 generation.</p>\n\n<h3 class=\"learn-subheading\">2. Dihybrid Cross</h3>\n<p class=\"learn-p\">A <span class=\"learn-keyword\">dihybrid cross</span> studies the inheritance of two traits simultaneously, such as seed shape and seed colour. When two heterozygous individuals (e.g., RrYy x RrYy) are crossed, the classic phenotypic ratio in the offspring is 9:3:3:1.</p>\n\n<h3 class=\"learn-subheading\">3. Using the Punnett Square</h3>\n<p class=\"learn-p\">A <span class=\"learn-keyword\">Punnett square</span> is a grid used to predict the possible genotypes and their probabilities in offspring, by combining the possible gametes from each parent along the top and side of the grid.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th></th><th>T</th><th>t</th></tr></thead>\n    <tbody>\n      <tr><td>T</td><td>TT</td><td>Tt</td></tr>\n      <tr><td>t</td><td>Tt</td><td>tt</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class=\"learn-p\">This example (Tt x Tt cross) predicts a genotype ratio of 1 TT : 2 Tt : 1 tt, giving a phenotype ratio of 3 tall : 1 short.</p>\n\n<h3 class=\"learn-subheading\">4. Sex Determination in Humans</h3>\n<p class=\"learn-p\">Humans have 23 pairs of chromosomes; one pair determines sex. Females have XX; males have XY. Since the mother always contributes an X, the father's contribution (X or Y) determines the offspring's sex.</p>\n\n<h3 class=\"learn-subheading\">5. Sex-Linked Traits</h3>\n<p class=\"learn-p\">Some traits are carried on the X chromosome and are called <span class=\"learn-keyword\">sex-linked traits</span>. Examples include haemophilia (a blood-clotting disorder) and colour blindness. Because males have only one X chromosome, they are more frequently affected by X-linked recessive conditions than females, who need two copies of the recessive allele to be affected.</p>\n\n<h3 class=\"learn-subheading\">6. Importance of Genetics</h3>\n<p class=\"learn-p\">Genetics is applied in agriculture (breeding higher-yielding crops and livestock) and medicine (understanding inherited disorders, genetic counselling, and blood transfusion compatibility).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Memorise the standard ratios: monohybrid F2 cross gives 3:1 phenotype ratio; dihybrid F2 cross gives 9:3:3:1 phenotype ratio. WAEC frequently asks you to complete a Punnett square or state the expected ratio.\n</div>\n",
    "questions": [
      {
        "q": "A monohybrid cross studies the inheritance of:",
        "o": [
          "Two traits simultaneously",
          "A single trait",
          "An entire chromosome set",
          "Only sex-linked traits"
        ],
        "a": 1,
        "e": "A monohybrid cross focuses on the inheritance pattern of a single trait, such as plant height.",
        "h": "Think of the prefix 'mono-' meaning 'one'.",
        "yr": "SS3"
      },
      {
        "q": "Crossing a homozygous tall plant (TT) with a homozygous short plant (tt) produces which F1 offspring?",
        "o": [
          "All TT",
          "All tt",
          "All Tt (tall)",
          "A mix of tall and short in equal numbers"
        ],
        "a": 2,
        "e": "Crossing TT with tt produces all heterozygous Tt offspring in the F1 generation, which are tall since T is dominant.",
        "h": "Each parent can only contribute one type of allele.",
        "yr": "SS3"
      },
      {
        "q": "Crossing two Tt (F1) plants together typically produces which phenotype ratio in the F2 generation?",
        "o": [
          "1:1",
          "9:3:3:1",
          "3:1 (tall:short)",
          "1:2:1"
        ],
        "a": 2,
        "e": "Crossing two heterozygous Tt plants typically produces a classic 3:1 phenotype ratio of tall to short in the F2 generation.",
        "h": "Recall the standard monohybrid cross ratio.",
        "yr": "SS3"
      },
      {
        "q": "A dihybrid cross studies the inheritance of:",
        "o": [
          "A single trait only",
          "Two traits simultaneously",
          "No traits at all",
          "Only chromosome number"
        ],
        "a": 1,
        "e": "A dihybrid cross studies the simultaneous inheritance of two different traits, such as seed shape and seed colour.",
        "h": "Think of the prefix 'di-' meaning 'two'.",
        "yr": "SS3"
      },
      {
        "q": "Crossing two heterozygous individuals for two traits (e.g., RrYy x RrYy) typically produces which classic phenotypic ratio?",
        "o": [
          "3:1",
          "1:1",
          "9:3:3:1",
          "1:2:1"
        ],
        "a": 2,
        "e": "The classic dihybrid cross ratio for two heterozygous parents (RrYy x RrYy) is 9:3:3:1 in the offspring phenotypes.",
        "h": "Recall the standard dihybrid cross ratio given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "A Punnett square is used to:",
        "o": [
          "Measure the size of a chromosome",
          "Predict the possible genotypes and their probabilities in offspring",
          "Directly observe a phenotype under a microscope",
          "Calculate the age of an organism"
        ],
        "a": 1,
        "e": "A Punnett square is a grid tool used to predict the possible genotypes and their probabilities in offspring, based on parental gametes.",
        "h": "Recall the definition of a Punnett square given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "In a Tt x Tt Punnett square cross, the resulting genotype ratio is:",
        "o": [
          "1 TT : 2 Tt : 1 tt",
          "All TT",
          "All tt",
          "2 TT : 1 Tt : 1 tt"
        ],
        "a": 0,
        "e": "A Tt x Tt cross produces a genotype ratio of 1 TT : 2 Tt : 1 tt, as shown in the Punnett square example.",
        "h": "Recall the worked Punnett square example in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "In humans, which sex chromosome combination determines a female?",
        "o": [
          "XY",
          "YY",
          "XX",
          "XO"
        ],
        "a": 2,
        "e": "In humans, females have the sex chromosome combination XX, while males have XY.",
        "h": "Recall the standard human sex chromosome pattern.",
        "yr": "SS3"
      },
      {
        "q": "In humans, which parent's genetic contribution determines the sex of the offspring?",
        "o": [
          "Always the mother's contribution",
          "Always the father's contribution, since the mother always contributes an X",
          "Neither parent affects offspring sex",
          "It is determined randomly with no genetic basis"
        ],
        "a": 1,
        "e": "Since the mother always contributes an X chromosome, the father's contribution (X or Y) determines whether the offspring will be female (XX) or male (XY).",
        "h": "Recall the explanation given for human sex determination.",
        "yr": "SS3"
      },
      {
        "q": "Haemophilia and colour blindness are examples of:",
        "o": [
          "Sex-linked traits carried on the X chromosome",
          "Traits with no genetic basis at all",
          "Traits only found in plants",
          "Autosomal dominant traits with no sex link"
        ],
        "a": 0,
        "e": "Haemophilia and colour blindness are classic examples of sex-linked traits, carried on the X chromosome.",
        "h": "Recall the two named examples given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Why are males more frequently affected by X-linked recessive conditions than females?",
        "o": [
          "Males have only one X chromosome, so a single recessive allele is enough to cause the condition",
          "Males have two X chromosomes, requiring two recessive alleles",
          "X-linked conditions never affect males",
          "There is no difference in how males and females are affected"
        ],
        "a": 0,
        "e": "Since males have only one X chromosome (XY), a single copy of a recessive X-linked allele is sufficient to cause the condition, unlike females who need two copies.",
        "h": "Recall the explanation given about X chromosome number differences.",
        "yr": "SS3"
      },
      {
        "q": "Genetics is applied in agriculture mainly to:",
        "o": [
          "Breed higher-yielding crops and livestock",
          "Study only wild, unmanaged ecosystems",
          "Prevent all crop breeding",
          "Eliminate the need for farming"
        ],
        "a": 0,
        "e": "In agriculture, genetics is applied to breed higher-yielding, more productive crops and livestock through selective breeding techniques.",
        "h": "Think of the practical agricultural application described.",
        "yr": "SS3"
      },
      {
        "q": "In medicine, understanding genetics helps with:",
        "o": [
          "Genetic counselling and blood transfusion compatibility",
          "Only cosmetic surgery",
          "Eliminating all diseases instantly",
          "Weather forecasting"
        ],
        "a": 0,
        "e": "Genetics is applied in medicine for purposes such as genetic counselling and determining blood transfusion compatibility.",
        "h": "Recall the medical applications listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "How many pairs of chromosomes do humans typically have in total?",
        "o": [
          "23 pairs",
          "46 individual chromosomes, described as pairs would be incorrect",
          "10 pairs",
          "100 pairs"
        ],
        "a": 0,
        "e": "Humans typically have 23 pairs of chromosomes (46 individual chromosomes total), with one pair determining sex.",
        "h": "Recall the number given in the lesson for human chromosome pairs.",
        "yr": "SS3"
      },
      {
        "q": "If a father contributes a Y chromosome during fertilization, the resulting offspring will be:",
        "o": [
          "Female (XX)",
          "Male (XY)",
          "Neither male nor female",
          "Determined only by the mother's chromosomes"
        ],
        "a": 1,
        "e": "If the father contributes a Y chromosome (combined with the mother's X), the resulting offspring will be male (XY).",
        "h": "Combine the mother's X with the father's Y.",
        "yr": "SS3"
      },
      {
        "q": "In a standard dihybrid cross Punnett square (RrYy x RrYy), how many boxes/combinations are typically used in the grid?",
        "o": [
          "4",
          "8",
          "16",
          "2"
        ],
        "a": 2,
        "e": "A dihybrid cross Punnett square typically uses a 4x4 grid (16 boxes), since each parent produces four possible gamete combinations.",
        "h": "Think about how many gamete types each RrYy parent can produce (2x2=4), then square that for the grid.",
        "yr": "SS3"
      },
      {
        "q": "Which best explains why a Tt x Tt cross does NOT produce a 1:1 phenotype ratio?",
        "o": [
          "Because the dominant allele masks the recessive allele in three out of four possible genotype combinations (TT, Tt, Tt), giving a 3:1 phenotype ratio",
          "Because Tt individuals cannot reproduce",
          "Because only recessive alleles are ever passed on",
          "Because dominant alleles are never inherited"
        ],
        "a": 0,
        "e": "Since T is dominant, the genotypes TT, Tt, and Tt (three out of four combinations) all show the tall phenotype, while only tt shows the short phenotype, giving a 3:1 ratio rather than 1:1.",
        "h": "Think about which genotypes share the same visible phenotype.",
        "yr": "SS3"
      },
      {
        "q": "A woman who is a carrier for an X-linked recessive condition (like haemophilia) but does not show symptoms herself has which genotype pattern?",
        "o": [
          "Homozygous recessive for the condition",
          "Heterozygous, with one normal allele masking the recessive allele",
          "She has no X chromosomes at all",
          "She has two Y chromosomes"
        ],
        "a": 1,
        "e": "A female carrier typically has one normal (dominant) allele and one recessive allele on her two X chromosomes, so the dominant allele masks the recessive condition, making her a symptomless carrier.",
        "h": "Think about how a female could carry but not express an X-linked recessive trait.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following correctly completes a Punnett square set-up for a monohybrid cross?",
        "o": [
          "Possible gametes from one parent are placed along the top, and from the other parent along the side",
          "All genotypes are placed randomly with no structure",
          "Only phenotypes, never genotypes, are placed in the grid",
          "The grid has no relationship to parental gametes"
        ],
        "a": 0,
        "e": "In a Punnett square, the possible gametes from one parent are placed along the top of the grid and the other parent's gametes along the side, with combinations filled into the boxes.",
        "h": "Recall the standard set-up of a Punnett square described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Why is understanding sex-linked inheritance patterns important in genetic counselling?",
        "o": [
          "It has no relevance to genetic counselling",
          "It helps predict the likelihood of X-linked conditions being passed to offspring, especially sons",
          "It only applies to plant breeding",
          "It eliminates the need for any further genetic testing"
        ],
        "a": 1,
        "e": "Understanding sex-linked inheritance helps genetic counsellors predict the likelihood of conditions like haemophilia being passed to offspring, particularly sons who are more vulnerable to X-linked recessive conditions.",
        "h": "Think about the practical value of this knowledge in family planning.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Variation and Evolution I",
    "topicCode": "SS3-BIO-04",
    "module": "Variation and Evolution",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Variation</span> refers to the differences that exist among individuals of the same species. These differences are the raw material upon which evolution acts, and they arise from several distinct causes.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Variation</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Description</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Morphological</td><td>Differences in physical structure or appearance</td><td>Eye colour, height, fingerprint patterns</td></tr>\n      <tr><td>Physiological</td><td>Differences in body function or metabolism</td><td>Blood group, enzyme activity, metabolic rate</td></tr>\n      <tr><td>Discontinuous</td><td>Traits that fall into distinct, separate categories with no intermediates</td><td>Blood group (A, B, AB, O), tongue rolling (can/cannot)</td></tr>\n      <tr><td>Continuous</td><td>Traits that vary gradually across a range with many intermediate values</td><td>Height, weight, skin colour</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Causes of Variation</h3>\n<ul class=\"learn-list\">\n  <li><strong>Mutation:</strong> Sudden, random changes in DNA that create new alleles</li>\n  <li><strong>Meiosis:</strong> The cell division process producing gametes shuffles genetic combinations through independent assortment and crossing over</li>\n  <li><strong>Fertilization:</strong> The random combination of genetic material from two parents during fertilization creates new genetic combinations in offspring</li>\n  <li><strong>Environment:</strong> External factors (nutrition, climate, exposure to sunlight) can influence how traits are expressed, though these changes are usually not inherited</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Adaptive Colouration, Mimicry, and Camouflage</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Description</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Camouflage</td><td>Colouring or pattern that helps an organism blend into its surroundings</td><td>Chameleons, stick insects</td></tr>\n      <tr><td>Mimicry</td><td>One species evolves to resemble another species, often a dangerous or unpalatable one, to avoid predation</td><td>Harmless hoverflies resembling stinging wasps</td></tr>\n      <tr><td>Warning colouration</td><td>Bright colours that signal an organism is dangerous or toxic to potential predators</td><td>Poison dart frogs</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Discontinuous variation gives clear, countable categories (e.g., blood group), while continuous variation forms a smooth range best shown on a graph as a bell curve (e.g., height in a population).\n</div>\n",
    "questions": [
      {
        "q": "Variation is best described as:",
        "o": [
          "The complete similarity among all individuals of a species",
          "The differences that exist among individuals of the same species",
          "The process of digestion",
          "A type of asexual reproduction"
        ],
        "a": 1,
        "e": "Variation refers to the differences observed among individuals belonging to the same species.",
        "h": "Focus on the word's core meaning: 'to vary' means to differ.",
        "yr": "SS3"
      },
      {
        "q": "Differences in physical structure or appearance, such as eye colour or height, are examples of:",
        "o": [
          "Physiological variation",
          "Morphological variation",
          "Discontinuous variation only",
          "No variation at all"
        ],
        "a": 1,
        "e": "Morphological variation refers to differences in physical structure or appearance, such as eye colour, height, or fingerprint patterns.",
        "h": "Think of 'morphology' as related to form/structure.",
        "yr": "SS3"
      },
      {
        "q": "Differences in body function, such as blood group or enzyme activity, are examples of:",
        "o": [
          "Morphological variation",
          "Physiological variation",
          "Continuous variation only",
          "Mutation"
        ],
        "a": 1,
        "e": "Physiological variation refers to differences in body function or metabolism, such as blood group or enzyme activity.",
        "h": "Think of 'physiology' as related to bodily function.",
        "yr": "SS3"
      },
      {
        "q": "Blood group (A, B, AB, O) is an example of which type of variation?",
        "o": [
          "Continuous variation",
          "Discontinuous variation",
          "Environmental variation only",
          "No variation"
        ],
        "a": 1,
        "e": "Blood group is a classic example of discontinuous variation, since it falls into distinct, separate categories with no intermediates.",
        "h": "Think about whether there are 'in-between' blood groups.",
        "yr": "SS3"
      },
      {
        "q": "Height in a human population is an example of which type of variation?",
        "o": [
          "Discontinuous variation",
          "Continuous variation",
          "Mutation only",
          "Sex-linked variation only"
        ],
        "a": 1,
        "e": "Height is a classic example of continuous variation, varying gradually across a range with many intermediate values.",
        "h": "Think about whether height forms distinct categories or a smooth range.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a cause of variation involving sudden, random changes in DNA?",
        "o": [
          "Mutation",
          "Camouflage",
          "Mimicry",
          "Warning colouration"
        ],
        "a": 0,
        "e": "Mutation refers to sudden, random changes in DNA that can create new alleles, contributing to genetic variation.",
        "h": "Recall the first cause of variation listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Meiosis contributes to variation mainly through:",
        "o": [
          "Producing exact genetic copies with no shuffling",
          "Shuffling genetic combinations via independent assortment and crossing over",
          "Preventing gamete formation entirely",
          "Eliminating all mutations"
        ],
        "a": 1,
        "e": "Meiosis contributes to variation by shuffling genetic combinations through processes like independent assortment and crossing over during gamete formation.",
        "h": "Recall how meiosis was described as a cause of variation.",
        "yr": "SS3"
      },
      {
        "q": "Fertilization contributes to variation because it:",
        "o": [
          "Always produces genetically identical offspring",
          "Randomly combines genetic material from two parents, creating new genetic combinations",
          "Prevents any genetic mixing",
          "Only occurs in asexual organisms"
        ],
        "a": 1,
        "e": "Fertilization creates new genetic combinations in offspring through the random combination of genetic material from two parents.",
        "h": "Think about what happens when two parents' genetic material combines.",
        "yr": "SS3"
      },
      {
        "q": "Environmental factors, such as nutrition and climate, can influence traits, but such changes are usually:",
        "o": [
          "Always inherited by the next generation",
          "Not inherited, since they don't alter the underlying genetic code",
          "The only cause of all variation",
          "Identical to mutations"
        ],
        "a": 1,
        "e": "Environmentally-induced changes in trait expression are usually not inherited, since they don't alter the organism's underlying genetic code (unlike mutations).",
        "h": "Recall the note distinguishing environmental influence from genetic causes.",
        "yr": "SS3"
      },
      {
        "q": "Camouflage is best described as:",
        "o": [
          "Bright colours signalling danger to predators",
          "Colouring or pattern that helps an organism blend into its surroundings",
          "One species resembling a dangerous species",
          "A type of genetic mutation only"
        ],
        "a": 1,
        "e": "Camouflage refers to colouring or patterns that help an organism blend into its environment, reducing detection by predators or prey.",
        "h": "Think of chameleons blending into leaves.",
        "yr": "SS3"
      },
      {
        "q": "Mimicry occurs when:",
        "o": [
          "An organism blends perfectly into a plain background",
          "One species evolves to resemble another species, often a dangerous one, to avoid predation",
          "An organism changes colour based on temperature only",
          "A species loses all its colour entirely"
        ],
        "a": 1,
        "e": "Mimicry occurs when one species evolves to resemble another, often dangerous or unpalatable, species to deter predators.",
        "h": "Recall the hoverfly/wasp example given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "The example of harmless hoverflies resembling stinging wasps illustrates:",
        "o": [
          "Camouflage",
          "Mimicry",
          "Warning colouration only",
          "Discontinuous variation"
        ],
        "a": 1,
        "e": "This is a classic example of mimicry, where a harmless species (hoverfly) evolves to resemble a dangerous one (wasp) to avoid being eaten.",
        "h": "Recall the specific example given for mimicry.",
        "yr": "SS3"
      },
      {
        "q": "Warning colouration, as seen in poison dart frogs, functions to:",
        "o": [
          "Help the organism hide completely from view",
          "Signal to potential predators that the organism is dangerous or toxic",
          "Attract only mates of a different species",
          "Disguise the organism as a plant"
        ],
        "a": 1,
        "e": "Warning colouration uses bright colours to signal to predators that an organism is dangerous or toxic, discouraging attacks.",
        "h": "Think about why poison dart frogs are brightly coloured rather than camouflaged.",
        "yr": "SS3"
      },
      {
        "q": "Which best distinguishes discontinuous from continuous variation?",
        "o": [
          "Discontinuous variation has clear, distinct categories; continuous variation forms a smooth range of values",
          "They are exactly the same type of variation",
          "Continuous variation always has only two categories",
          "Discontinuous variation never has genetic causes"
        ],
        "a": 0,
        "e": "Discontinuous variation involves clear, distinct categories with no intermediates, while continuous variation forms a smooth, gradual range of values.",
        "h": "Recall the exam tip distinguishing these two variation types.",
        "yr": "SS3"
      },
      {
        "q": "Tongue rolling (the ability to roll one's tongue or not) is an example of:",
        "o": [
          "Continuous variation",
          "Discontinuous variation",
          "Environmental variation only",
          "Mimicry"
        ],
        "a": 1,
        "e": "Tongue rolling is a discontinuous trait — you either can or cannot roll your tongue, with no intermediate categories.",
        "h": "Recall the example given for discontinuous variation.",
        "yr": "SS3"
      },
      {
        "q": "When graphed across a population, continuous variation typically produces:",
        "o": [
          "A bell-shaped curve showing a smooth range of values",
          "Only two separate bars with no range",
          "A single point with no spread",
          "No graphical pattern at all"
        ],
        "a": 0,
        "e": "Continuous variation typically produces a bell-shaped curve when graphed, reflecting the smooth range of values across a population, such as height.",
        "h": "Recall the exam tip's description of how continuous variation is shown graphically.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a genetic (heritable) cause of variation, as opposed to an environmental one?",
        "o": [
          "Sunburn from excessive sun exposure",
          "Mutation in the DNA sequence",
          "Reduced growth due to poor nutrition",
          "Muscle gain from exercise"
        ],
        "a": 1,
        "e": "Mutation is a genetic, heritable cause of variation since it alters the DNA sequence itself, unlike purely environmental influences like sunburn or exercise.",
        "h": "Look for the option that changes the actual genetic code.",
        "yr": "SS3"
      },
      {
        "q": "Adaptive colouration such as camouflage and mimicry ultimately serves to:",
        "o": [
          "Increase an organism's chances of survival and reproduction",
          "Have no impact on survival at all",
          "Only benefit predators, never prey",
          "Prevent all reproduction in a species"
        ],
        "a": 0,
        "e": "Adaptive colouration strategies like camouflage and mimicry increase an organism's chances of survival (by avoiding predation), ultimately improving reproductive success.",
        "h": "Think about the evolutionary benefit of these adaptations.",
        "yr": "SS3"
      },
      {
        "q": "Which type of variation would enzyme activity level in an organism's metabolism most likely represent?",
        "o": [
          "Morphological variation",
          "Physiological variation",
          "Discontinuous variation only, with no physiological basis",
          "Camouflage"
        ],
        "a": 1,
        "e": "Enzyme activity, relating to body function and metabolism, is classified as physiological variation.",
        "h": "Recall the category associated with body function examples.",
        "yr": "SS3"
      },
      {
        "q": "Why is variation considered essential 'raw material' for evolution?",
        "o": [
          "Because natural selection acts on existing differences among individuals to favour advantageous traits",
          "Because variation prevents any evolutionary change from occurring",
          "Because all individuals within a species must be genetically identical for evolution to occur",
          "Variation has no connection to evolution at all"
        ],
        "a": 0,
        "e": "Variation provides the differences among individuals that natural selection can act upon, favouring advantageous traits — making it essential raw material for evolution.",
        "h": "Think about the connection drawn in the lesson's introduction between variation and evolution.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Evolution II",
    "topicCode": "SS3-BIO-05",
    "module": "Variation and Evolution",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Evolution</span> is the gradual change in the inherited characteristics of populations over successive generations. This topic examines the major theories that attempt to explain how evolution occurs, and the evidence supporting it.\n</div>\n\n<h3 class=\"learn-subheading\">1. Lamarckism (Theory of Inheritance of Acquired Characteristics)</h3>\n<p class=\"learn-p\">Jean-Baptiste Lamarck proposed that organisms could pass on characteristics acquired during their lifetime to their offspring. His classic example: giraffes stretching their necks to reach high leaves, and this \"stretched\" trait being passed to their young. This theory has been largely discredited, since acquired traits are not encoded in DNA and therefore cannot be inherited.</p>\n\n<h3 class=\"learn-subheading\">2. Darwinism (Theory of Natural Selection)</h3>\n<p class=\"learn-p\">Charles Darwin proposed that evolution occurs through <span class=\"learn-keyword\">natural selection</span>: individuals with favourable inherited variations are more likely to survive and reproduce, passing those advantageous traits to the next generation. Over many generations, this leads to gradual change in the population. Unlike Lamarck, Darwin's theory relies on existing heritable variation, not traits acquired during an individual's life.</p>\n\n<h3 class=\"learn-subheading\">3. Modern Synthetic Theory of Evolution</h3>\n<p class=\"learn-p\">The modern synthetic theory combines Darwin's natural selection with Mendelian genetics and modern understanding of mutation, explaining how new genetic variation arises (mutation) and how natural selection acts on that variation to drive evolutionary change.</p>\n\n<h3 class=\"learn-subheading\">4. Evidence for Evolution</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Evidence Type</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Fossil records</td><td>Preserved remains showing gradual changes in organisms over geological time</td></tr>\n      <tr><td>Comparative anatomy</td><td>Similar skeletal structures in different species (e.g., homologous limb bones) suggesting a common ancestor</td></tr>\n      <tr><td>Embryology</td><td>Similarities in the early embryonic development of different species, suggesting shared ancestry</td></tr>\n      <tr><td>Vestigial structures</td><td>Reduced, functionless body parts that were once useful in an ancestor (e.g., the human appendix)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">5. Natural Selection and Adaptation</h3>\n<p class=\"learn-p\">Natural selection favours individuals whose traits are best suited (adapted) to their environment. Over time, this process increases the frequency of advantageous traits within a population, improving its overall fitness for survival in that environment.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Don't confuse Lamarckism (acquired traits inherited) with Darwinism (existing heritable variation selected for). WAEC often asks you to identify which scientist proposed which theory.\n</div>\n",
    "questions": [
      {
        "q": "Evolution is best defined as:",
        "o": [
          "A sudden, one-time change within a single organism's lifetime",
          "The gradual change in the inherited characteristics of populations over successive generations",
          "The process of digestion in animals",
          "A type of asexual reproduction"
        ],
        "a": 1,
        "e": "Evolution is the gradual change in the inherited characteristics of populations over many successive generations.",
        "h": "Focus on 'gradual' and 'populations over generations', not a single organism.",
        "yr": "SS3"
      },
      {
        "q": "Jean-Baptiste Lamarck's theory proposed that:",
        "o": [
          "Only random mutations cause evolution",
          "Organisms could pass on characteristics acquired during their lifetime to their offspring",
          "Natural selection is the sole driver of evolution",
          "Species never change over time"
        ],
        "a": 1,
        "e": "Lamarck proposed the inheritance of acquired characteristics — traits gained during an organism's lifetime being passed to offspring.",
        "h": "Recall the giraffe neck-stretching example given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Lamarck's classic example involved giraffes:",
        "o": [
          "Losing their necks over time",
          "Stretching their necks to reach high leaves and passing this trait to offspring",
          "Migrating to new continents",
          "Developing wings to fly"
        ],
        "a": 1,
        "e": "Lamarck's classic example was giraffes stretching their necks to reach high leaves, with this acquired trait supposedly passed to their offspring.",
        "h": "Recall the specific animal example used in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Lamarck's theory has been largely discredited mainly because:",
        "o": [
          "Acquired traits are not encoded in DNA and therefore cannot be inherited",
          "Giraffes do not actually have long necks",
          "DNA does not exist",
          "Natural selection has also been discredited"
        ],
        "a": 0,
        "e": "Lamarck's theory is discredited because acquired characteristics, gained during an organism's life, are not encoded in its DNA and therefore cannot be passed on genetically.",
        "h": "Think about what modern genetics tells us about inheritance.",
        "yr": "SS3"
      },
      {
        "q": "Charles Darwin's theory of evolution is based on the concept of:",
        "o": [
          "Inheritance of acquired characteristics",
          "Natural selection acting on existing heritable variation",
          "Spontaneous generation of new species",
          "Only human evolution"
        ],
        "a": 1,
        "e": "Darwin's theory of evolution is based on natural selection acting on existing heritable variation within a population.",
        "h": "Recall the key mechanism proposed by Darwin.",
        "yr": "SS3"
      },
      {
        "q": "According to Darwin's theory, individuals with favourable inherited variations are more likely to:",
        "o": [
          "Die out immediately",
          "Survive and reproduce, passing those traits to the next generation",
          "Lose all their offspring",
          "Never pass on any traits"
        ],
        "a": 1,
        "e": "Darwin proposed that individuals with favourable inherited variations are more likely to survive and reproduce, passing those advantageous traits on to the next generation.",
        "h": "Think of the core idea behind 'survival of the fittest'.",
        "yr": "SS3"
      },
      {
        "q": "A key difference between Darwinism and Lamarckism is that Darwinism relies on:",
        "o": [
          "Traits acquired during an individual's lifetime",
          "Existing heritable variation, not traits acquired during an individual's life",
          "Spontaneous mutation only, with no selection",
          "No genetic basis at all"
        ],
        "a": 1,
        "e": "Unlike Lamarckism, Darwin's theory relies on existing heritable variation within a population, upon which natural selection acts — not traits acquired during an individual's lifetime.",
        "h": "Recall the exam tip distinguishing the two theories.",
        "yr": "SS3"
      },
      {
        "q": "The modern synthetic theory of evolution combines Darwin's natural selection with:",
        "o": [
          "Mendelian genetics and modern understanding of mutation",
          "Lamarck's theory of acquired characteristics only",
          "No genetic understanding at all",
          "Only fossil evidence"
        ],
        "a": 0,
        "e": "The modern synthetic theory combines Darwin's natural selection with Mendelian genetics and modern understanding of mutation.",
        "h": "Recall what the modern theory adds to Darwin's original idea.",
        "yr": "SS3"
      },
      {
        "q": "Fossil records serve as evidence for evolution because they:",
        "o": [
          "Show gradual changes in organisms over geological time",
          "Prove that no organisms have ever changed",
          "Only show modern-day organisms",
          "Have no connection to evolutionary history"
        ],
        "a": 0,
        "e": "Fossil records provide evidence for evolution by showing the gradual changes in organisms preserved over geological time.",
        "h": "Recall the first evidence type listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Comparative anatomy, such as similar homologous limb bones across species, suggests:",
        "o": [
          "That species share no evolutionary relationship",
          "A common ancestor shared by different species",
          "That all species are identical",
          "That evolution does not occur"
        ],
        "a": 1,
        "e": "Similar skeletal structures (homologous structures) across different species suggest they share a common evolutionary ancestor.",
        "h": "Recall the explanation given for comparative anatomy as evidence.",
        "yr": "SS3"
      },
      {
        "q": "Similarities in early embryonic development across different species provide evidence for evolution through the field of:",
        "o": [
          "Embryology",
          "Fossil records",
          "Vestigial structures",
          "Comparative anatomy only"
        ],
        "a": 0,
        "e": "Embryology studies similarities in early embryonic development across species, which suggests shared evolutionary ancestry.",
        "h": "Recall the specific evidence type related to embryo development.",
        "yr": "SS3"
      },
      {
        "q": "The human appendix is often cited as an example of a:",
        "o": [
          "Fossil record",
          "Vestigial structure — a reduced, functionless part once useful in an ancestor",
          "Sex-linked trait",
          "Form of mimicry"
        ],
        "a": 1,
        "e": "The human appendix is a classic example of a vestigial structure — a reduced body part that was likely more functional in an evolutionary ancestor.",
        "h": "Recall the example given for vestigial structures.",
        "yr": "SS3"
      },
      {
        "q": "Natural selection favours individuals whose traits are:",
        "o": [
          "Best suited (adapted) to their environment",
          "Randomly chosen with no relation to environment",
          "Always identical to their parents' traits with no variation",
          "Only related to physical strength, regardless of environment"
        ],
        "a": 0,
        "e": "Natural selection favours individuals whose traits are best adapted to their specific environment, improving their chances of survival and reproduction.",
        "h": "Think of the core principle of 'survival of the fittest' in context.",
        "yr": "SS3"
      },
      {
        "q": "Over time, natural selection tends to increase the frequency of:",
        "o": [
          "Disadvantageous traits only",
          "Advantageous traits within a population",
          "No particular traits, since selection is random",
          "Traits acquired during an individual's lifetime only"
        ],
        "a": 1,
        "e": "Over successive generations, natural selection tends to increase the frequency of advantageous, well-adapted traits within a population.",
        "h": "Recall the described outcome of the natural selection process.",
        "yr": "SS3"
      },
      {
        "q": "Which scientist is correctly associated with the theory of inheritance of acquired characteristics?",
        "o": [
          "Charles Darwin",
          "Gregor Mendel",
          "Jean-Baptiste Lamarck",
          "Watson and Crick"
        ],
        "a": 2,
        "e": "Jean-Baptiste Lamarck is correctly associated with the theory of inheritance of acquired characteristics.",
        "h": "Recall the scientist's name linked to this specific theory.",
        "yr": "SS3"
      },
      {
        "q": "Which scientist proposed the theory of natural selection as the driving mechanism of evolution?",
        "o": [
          "Jean-Baptiste Lamarck",
          "Charles Darwin",
          "Gregor Mendel",
          "None of the above"
        ],
        "a": 1,
        "e": "Charles Darwin proposed the theory of natural selection as the mechanism driving evolutionary change.",
        "h": "Recall the scientist most associated with 'survival of the fittest'.",
        "yr": "SS3"
      },
      {
        "q": "Why does the modern synthetic theory represent an improvement over Darwin's original theory alone?",
        "o": [
          "It incorporates genetic understanding of how variation arises through mutation, which Darwin could not explain",
          "It completely rejects natural selection",
          "It reintroduces Lamarck's theory as fully correct",
          "It removes the concept of heritable variation entirely"
        ],
        "a": 0,
        "e": "The modern synthetic theory improves on Darwin's original theory by incorporating genetic understanding — explaining how new variation arises through mutation, which Darwin himself could not fully explain.",
        "h": "Think about what genetics added to Darwin's original framework.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is NOT listed in the lesson as a form of evidence for evolution?",
        "o": [
          "Fossil records",
          "Comparative anatomy",
          "Embryology",
          "Horoscope readings"
        ],
        "a": 3,
        "e": "Horoscope readings are not a scientific form of evidence for evolution and are not listed among the evidence types in the lesson.",
        "h": "Look for the option that is not a genuine scientific evidence type.",
        "yr": "SS3"
      },
      {
        "q": "A population that becomes better adapted to its environment over many generations, due to natural selection, demonstrates:",
        "o": [
          "Stagnation with no change",
          "An increase in overall population fitness for survival in that environment",
          "The complete extinction of the species",
          "A reversal of evolutionary progress"
        ],
        "a": 1,
        "e": "As natural selection favours advantageous traits, a population's overall fitness for survival in its environment tends to increase over generations.",
        "h": "Recall the described long-term effect of natural selection.",
        "yr": "SS3"
      },
      {
        "q": "Which statement correctly summarises the key flaw in Lamarck's theory, according to modern genetics?",
        "o": [
          "Traits gained through use or environment during an organism's life are not passed on genetically to offspring",
          "Mutation does not exist",
          "Natural selection is entirely false",
          "DNA does not carry hereditary information"
        ],
        "a": 0,
        "e": "Modern genetics shows that traits gained through use, disuse, or environmental influence during an individual's lifetime are not encoded in DNA and therefore cannot be inherited — the key flaw in Lamarck's theory.",
        "h": "Recall the reason given for why Lamarckism was discredited.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Human Evolution",
    "topicCode": "SS3-BIO-06",
    "module": "Variation and Evolution",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Human evolution</span> traces the biological and cultural development of modern humans (Homo sapiens) from earlier hominid ancestors, supported by fossil evidence spanning millions of years.\n</div>\n\n<h3 class=\"learn-subheading\">1. The Origin of Man</h3>\n<p class=\"learn-p\">Scientific evidence indicates that modern humans share a common ancestry with other primates, particularly apes, and that the hominid lineage evolved in Africa over millions of years before spreading to other continents.</p>\n\n<h3 class=\"learn-subheading\">2. Key Stages in Human Fossil Evidence</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Species</th><th>Key Features</th></tr></thead>\n    <tbody>\n      <tr><td>Australopithecus</td><td>Early hominid, walked upright (bipedal) but had a small brain, ape-like face; lived several million years ago in Africa</td></tr>\n      <tr><td>Homo habilis</td><td>\"Handy man\" — larger brain than Australopithecus, associated with early simple stone tool use</td></tr>\n      <tr><td>Homo erectus</td><td>\"Upright man\" — larger brain, more advanced tools, controlled use of fire, first to migrate out of Africa</td></tr>\n      <tr><td>Homo sapiens</td><td>Modern humans — largest brain size, complex language, advanced tool use, art, and culture</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Similarities Between Humans and Apes</h3>\n<p class=\"learn-p\">Humans and apes (chimpanzees, gorillas) share many anatomical and genetic similarities, including similar skeletal structure, grasping hands, complex social behaviour, and a very high percentage of shared DNA — evidence of a common evolutionary ancestor.</p>\n\n<h3 class=\"learn-subheading\">4. Biological vs. Cultural Evolution</h3>\n<ul class=\"learn-list\">\n  <li><strong>Biological evolution:</strong> Physical changes in the human body over generations, such as increased brain size, upright posture, and changes in skeletal structure, driven by natural selection and genetics</li>\n  <li><strong>Cultural evolution:</strong> The development of tools, language, social organisation, art, and technology, passed on through learning rather than genes — allowing rapid adaptation without waiting for genetic change</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember the general trend across hominid species: brain size and tool complexity generally increased over time from Australopithecus to Homo sapiens. WAEC often asks candidates to sequence these species correctly.\n</div>\n",
    "questions": [
      {
        "q": "Fossil evidence indicates that the hominid lineage evolved primarily in:",
        "o": [
          "Europe",
          "Africa",
          "Asia",
          "South America"
        ],
        "a": 1,
        "e": "Fossil evidence indicates that the hominid lineage evolved primarily in Africa before later spreading to other continents.",
        "h": "Recall the continent identified as the origin of human evolution.",
        "yr": "SS3"
      },
      {
        "q": "Australopithecus is characterised by:",
        "o": [
          "A large brain and advanced stone tools",
          "Walking upright (bipedal) but having a small brain and ape-like face",
          "Complex language and art",
          "Controlled use of fire"
        ],
        "a": 1,
        "e": "Australopithecus was an early hominid that walked upright but had a relatively small brain and an ape-like face.",
        "h": "Recall the key features listed for this earliest species in the table.",
        "yr": "SS3"
      },
      {
        "q": "Homo habilis is often nicknamed:",
        "o": [
          "'Upright man'",
          "'Handy man'",
          "'Wise man'",
          "'Ancient man'"
        ],
        "a": 1,
        "e": "Homo habilis is nicknamed 'handy man' due to its association with early simple stone tool use.",
        "h": "Think of a nickname relating to tool use.",
        "yr": "SS3"
      },
      {
        "q": "Which species is associated with the controlled use of fire and being the first to migrate out of Africa?",
        "o": [
          "Australopithecus",
          "Homo habilis",
          "Homo erectus",
          "Homo sapiens"
        ],
        "a": 2,
        "e": "Homo erectus ('upright man') is associated with controlled use of fire, more advanced tools, and being the first hominid species to migrate out of Africa.",
        "h": "Recall the species linked to fire use and migration.",
        "yr": "SS3"
      },
      {
        "q": "Homo sapiens, the modern human species, is characterised by:",
        "o": [
          "The smallest brain size among hominids",
          "The largest brain size, complex language, advanced tool use, art, and culture",
          "A complete absence of tool use",
          "No social behaviour at all"
        ],
        "a": 1,
        "e": "Homo sapiens (modern humans) are characterised by the largest brain size among hominids, along with complex language, advanced tools, art, and culture.",
        "h": "Recall the features listed for the most recent species in the table.",
        "yr": "SS3"
      },
      {
        "q": "Which of these correctly orders hominid species from earliest to most recent, based on the lesson?",
        "o": [
          "Homo sapiens, Homo erectus, Homo habilis, Australopithecus",
          "Australopithecus, Homo habilis, Homo erectus, Homo sapiens",
          "Homo habilis, Australopithecus, Homo sapiens, Homo erectus",
          "Homo erectus, Australopithecus, Homo habilis, Homo sapiens"
        ],
        "a": 1,
        "e": "The correct evolutionary sequence, from earliest to most recent, is Australopithecus, Homo habilis, Homo erectus, and finally Homo sapiens.",
        "h": "Recall the order presented in the fossil evidence table.",
        "yr": "SS3"
      },
      {
        "q": "Humans and apes share many similarities, including:",
        "o": [
          "Completely different skeletal structures with no overlap",
          "Similar skeletal structure, grasping hands, and complex social behaviour",
          "No shared DNA whatsoever",
          "Only superficial physical resemblance with no genetic basis"
        ],
        "a": 1,
        "e": "Humans and apes share similar skeletal structures, grasping hands, complex social behaviour, and a very high percentage of shared DNA.",
        "h": "Recall the specific similarities listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "The high percentage of shared DNA between humans and apes is considered evidence for:",
        "o": [
          "A common evolutionary ancestor",
          "Completely separate, unrelated origins",
          "The theory that apes evolved directly from humans",
          "No evolutionary relationship at all"
        ],
        "a": 0,
        "e": "The very high percentage of shared DNA between humans and apes is considered strong evidence of a common evolutionary ancestor.",
        "h": "Think about what shared genetic material implies about ancestry.",
        "yr": "SS3"
      },
      {
        "q": "Biological evolution in humans refers to:",
        "o": [
          "The development of tools and language passed through learning",
          "Physical changes in the human body over generations, driven by natural selection and genetics",
          "Only cultural traditions with no physical basis",
          "A single individual's lifetime achievements"
        ],
        "a": 1,
        "e": "Biological evolution refers to physical changes in the human body over generations, such as increased brain size, driven by natural selection and genetics.",
        "h": "Focus on physical, genetically-driven changes.",
        "yr": "SS3"
      },
      {
        "q": "Cultural evolution in humans refers to:",
        "o": [
          "Changes to DNA passed on genetically",
          "The development of tools, language, social organisation, art, and technology, passed on through learning",
          "A decrease in brain size over time",
          "The physical process of bone growth"
        ],
        "a": 1,
        "e": "Cultural evolution refers to the development of tools, language, and technology, passed on through learning and teaching rather than through genes.",
        "h": "Focus on things learned and taught, not inherited genetically.",
        "yr": "SS3"
      },
      {
        "q": "A key advantage of cultural evolution over purely biological evolution is that it allows:",
        "o": [
          "Rapid adaptation without waiting for genetic change",
          "Slower adaptation than biological evolution",
          "No adaptation at all",
          "Only physical, not behavioural, change"
        ],
        "a": 0,
        "e": "Cultural evolution allows for rapid adaptation to new challenges through learning and technology, without needing to wait for slow genetic changes.",
        "h": "Think about the speed advantage of learned behaviour versus genetic change.",
        "yr": "SS3"
      },
      {
        "q": "According to the general trend across hominid species, brain size and tool complexity:",
        "o": [
          "Decreased over time from Australopithecus to Homo sapiens",
          "Generally increased over time from Australopithecus to Homo sapiens",
          "Remained exactly the same throughout human evolution",
          "Had no relationship to each other"
        ],
        "a": 1,
        "e": "The general evolutionary trend shows both brain size and tool complexity generally increasing over time, from Australopithecus to Homo sapiens.",
        "h": "Recall the exam tip's summary of the overall trend.",
        "yr": "SS3"
      },
      {
        "q": "Which species used only early, simple stone tools, as opposed to more advanced tools?",
        "o": [
          "Homo sapiens",
          "Homo erectus",
          "Homo habilis",
          "None of the hominid species used tools"
        ],
        "a": 2,
        "e": "Homo habilis is specifically associated with early, simple stone tool use, distinguishing it from the more advanced tools of later species.",
        "h": "Recall the species linked to the earliest simple tools.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an example of cultural (not biological) evolution in humans?",
        "o": [
          "An increase in average brain size over many generations",
          "The invention and spread of written language",
          "A genetic mutation altering skeletal structure",
          "Natural selection favouring a particular skin tone in a climate"
        ],
        "a": 1,
        "e": "The invention and spread of written language is a learned, transmitted cultural development, not a genetically inherited biological change.",
        "h": "Look for the option that is learned/taught rather than genetically inherited.",
        "yr": "SS3"
      },
      {
        "q": "Which best describes Homo erectus compared to Australopithecus?",
        "o": [
          "Homo erectus had a smaller brain and less advanced tools",
          "Homo erectus had a larger brain, more advanced tools, and used fire, unlike Australopithecus",
          "They are exactly identical species",
          "Australopithecus migrated out of Africa first"
        ],
        "a": 1,
        "e": "Homo erectus had a larger brain, more advanced tools, and controlled use of fire compared to the earlier Australopithecus, and was the first to migrate out of Africa.",
        "h": "Compare the features listed for both species in the table.",
        "yr": "SS3"
      },
      {
        "q": "The study of human evolution relies heavily on which type of evidence?",
        "o": [
          "Fossil evidence of hominid species",
          "Horoscope predictions",
          "Modern weather patterns",
          "Random guessing with no evidence"
        ],
        "a": 0,
        "e": "The study of human evolution relies heavily on fossil evidence documenting the physical features of various hominid species over time.",
        "h": "Recall the type of evidence emphasised throughout the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Bipedalism (walking upright on two legs) is a feature first noted in which hominid species discussed?",
        "o": [
          "Homo sapiens only",
          "Australopithecus",
          "Homo erectus only",
          "No hominid species show bipedalism"
        ],
        "a": 1,
        "e": "Australopithecus is noted for already walking upright (bipedal), despite retaining a small brain and ape-like face — one of the earliest evidences of bipedalism.",
        "h": "Recall the earliest species in the table and its listed features.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would be classified as an example of biological evolution rather than cultural evolution?",
        "o": [
          "The development of complex written laws",
          "A gradual increase in average brain size across hominid species over millions of years",
          "The spread of religious traditions",
          "The invention of the wheel"
        ],
        "a": 1,
        "e": "A gradual increase in brain size across hominid species reflects a genetically-driven physical change over generations — biological evolution, not a learned cultural development.",
        "h": "Look for the option describing a physical, inherited trait change.",
        "yr": "SS3"
      },
      {
        "q": "Why do scientists consider similarities in skeletal structure between humans and apes significant?",
        "o": [
          "They are considered coincidental and hold no scientific significance",
          "They provide comparative anatomical evidence supporting a shared evolutionary ancestry",
          "They prove humans and apes are identical species",
          "They disprove the theory of evolution entirely"
        ],
        "a": 1,
        "e": "Similarities in skeletal structure between humans and apes provide comparative anatomical evidence supporting the idea of a shared evolutionary ancestor, consistent with broader evolutionary evidence.",
        "h": "Connect this to the comparative anatomy evidence discussed in the previous topic.",
        "yr": "SS3"
      },
      {
        "q": "Which statement best summarises the overall theme of human evolution as covered in this lesson?",
        "o": [
          "Humans appeared suddenly with no connection to earlier species",
          "Modern humans evolved gradually from earlier hominid ancestors through both biological and cultural changes",
          "Human evolution has nothing to do with genetics",
          "Only cultural, never biological, change has occurred in human history"
        ],
        "a": 1,
        "e": "The lesson's overall theme is that modern humans evolved gradually from earlier hominid ancestors through a combination of biological changes (like brain size) and cultural developments (like tool use and language).",
        "h": "Think about the combined biological and cultural narrative presented throughout the lesson.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Application of Genetics and Evolution",
    "topicCode": "SS3-BIO-07",
    "module": "Applications and Implications",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The principles of <span class=\"learn-keyword\">genetics and evolution</span> are not just theoretical — they have practical, real-world applications in agriculture, medicine, and conservation that directly affect food production, healthcare, and biodiversity.\n</div>\n\n<h3 class=\"learn-subheading\">1. Applications in Agriculture</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Application</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Selective breeding</td><td>Choosing parent organisms with desirable traits (high yield, disease resistance) to breed the next generation</td></tr>\n      <tr><td>Hybridization</td><td>Crossing two different varieties or species to combine desirable traits in the offspring</td></tr>\n      <tr><td>Genetic engineering</td><td>Directly altering an organism's DNA to introduce or remove specific traits</td></tr>\n      <tr><td>GMOs</td><td>Genetically Modified Organisms — organisms whose genetic material has been altered using genetic engineering techniques, e.g., pest-resistant crops</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Applications in Medicine</h3>\n<ul class=\"learn-list\">\n  <li><strong>Blood transfusion:</strong> Understanding blood group genetics (A, B, AB, O) ensures compatible, safe transfusions between donor and recipient</li>\n  <li><strong>Sickle cell study:</strong> Genetics research has revealed how the sickle cell allele is inherited and how it relates to malaria resistance, informing screening and counselling programmes</li>\n  <li><strong>Genetic counselling:</strong> Advising couples on the risk of passing on inherited disorders to their children, based on genetic analysis</li>\n  <li><strong>Cloning:</strong> Producing genetically identical copies of cells or organisms, with potential medical applications in tissue repair and research</li>\n  <li><strong>IVF (In Vitro Fertilization):</strong> A medical technique where fertilization occurs outside the body, helping couples facing fertility challenges to conceive</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Role of Evolution in Biodiversity and Conservation</h3>\n<p class=\"learn-p\">Understanding evolutionary relationships helps conservationists prioritise which species and genetic diversity to protect, predict how species may respond to environmental change (such as climate change), and manage breeding programmes for endangered species to maintain healthy genetic diversity.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Distinguish \"selective breeding\" (choosing existing organisms with desired traits to breed) from \"genetic engineering\" (directly modifying DNA) — WAEC frequently tests this distinction.\n</div>\n",
    "questions": [
      {
        "q": "Selective breeding involves:",
        "o": [
          "Directly altering an organism's DNA in a laboratory",
          "Choosing parent organisms with desirable traits to breed the next generation",
          "Cloning an organism exactly",
          "Preventing all reproduction in a species"
        ],
        "a": 1,
        "e": "Selective breeding involves choosing parent organisms with desirable traits, such as high yield or disease resistance, to produce offspring with those traits.",
        "h": "Think of farmers choosing the best animals or plants to breed.",
        "yr": "SS3"
      },
      {
        "q": "Hybridization in agriculture refers to:",
        "o": [
          "Crossing two different varieties or species to combine desirable traits",
          "Cloning a single organism repeatedly",
          "Directly editing genes in a laboratory",
          "Preventing cross-breeding entirely"
        ],
        "a": 0,
        "e": "Hybridization involves crossing two different varieties or species to combine their desirable traits in the resulting offspring.",
        "h": "Think of combining traits from two different parent types.",
        "yr": "SS3"
      },
      {
        "q": "Genetic engineering is best described as:",
        "o": [
          "Selecting existing organisms with favourable traits only",
          "Directly altering an organism's DNA to introduce or remove specific traits",
          "A form of natural selection with no human involvement",
          "A type of blood transfusion"
        ],
        "a": 1,
        "e": "Genetic engineering involves directly altering an organism's DNA in a laboratory to introduce or remove specific traits.",
        "h": "Focus on the direct, laboratory-based DNA modification.",
        "yr": "SS3"
      },
      {
        "q": "GMOs stands for:",
        "o": [
          "Generally Modified Organisms",
          "Genetically Modified Organisms",
          "Genetic Mutation Occurrences",
          "General Medical Observations"
        ],
        "a": 1,
        "e": "GMO stands for Genetically Modified Organisms — organisms whose genetic material has been altered using genetic engineering techniques.",
        "h": "Break down the acronym letter by letter.",
        "yr": "SS3"
      },
      {
        "q": "Pest-resistant crops created through genetic engineering are an example of:",
        "o": [
          "Selective breeding only",
          "GMOs",
          "Cloning",
          "IVF"
        ],
        "a": 1,
        "e": "Pest-resistant crops produced through direct genetic engineering are a classic example of GMOs (Genetically Modified Organisms).",
        "h": "Recall the example given in the lesson for GMOs.",
        "yr": "SS3"
      },
      {
        "q": "Understanding blood group genetics is important in medicine mainly for:",
        "o": [
          "Ensuring compatible, safe blood transfusions between donor and recipient",
          "Predicting weather patterns",
          "Determining an individual's height",
          "Curing all viral infections"
        ],
        "a": 0,
        "e": "Blood group genetics knowledge ensures that blood transfusions between donors and recipients are compatible and safe.",
        "h": "Think about why matching blood types matters medically.",
        "yr": "SS3"
      },
      {
        "q": "Sickle cell genetics research has revealed a connection between the sickle cell allele and:",
        "o": [
          "Increased risk of malaria with no benefit",
          "Malaria resistance",
          "Blood transfusion incompatibility only",
          "No medical relevance at all"
        ],
        "a": 1,
        "e": "Genetics research has shown that carrying the sickle cell allele is associated with resistance to malaria, an important insight for screening and counselling.",
        "h": "Recall the specific medical relevance mentioned regarding sickle cell genetics.",
        "yr": "SS3"
      },
      {
        "q": "Genetic counselling primarily involves:",
        "o": [
          "Advising couples on the risk of passing on inherited disorders to their children",
          "Performing surgery on genetic disorders",
          "Predicting future weather events",
          "Cloning organisms for research"
        ],
        "a": 0,
        "e": "Genetic counselling involves advising couples about the risk of passing on inherited genetic disorders to their children, based on genetic analysis.",
        "h": "Recall the definition given for genetic counselling.",
        "yr": "SS3"
      },
      {
        "q": "Cloning is best described as:",
        "o": [
          "Crossing two different species",
          "Producing genetically identical copies of cells or organisms",
          "A method of blood transfusion",
          "A type of natural selection"
        ],
        "a": 1,
        "e": "Cloning involves producing genetically identical copies of cells or organisms, with applications in tissue repair and research.",
        "h": "Think of creating exact genetic copies.",
        "yr": "SS3"
      },
      {
        "q": "IVF (In Vitro Fertilization) is a medical technique that:",
        "o": [
          "Occurs entirely within the mother's body with no external steps",
          "Involves fertilization occurring outside the body, helping couples facing fertility challenges",
          "Is unrelated to genetics or reproduction",
          "Always results in genetically identical offspring, like cloning"
        ],
        "a": 1,
        "e": "IVF is a technique where fertilization takes place outside the body, helping couples facing fertility challenges to conceive.",
        "h": "Recall the definition of IVF given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Understanding evolutionary relationships helps conservationists mainly by:",
        "o": [
          "Having no practical conservation value",
          "Helping prioritise which species and genetic diversity to protect",
          "Preventing all species from going extinct automatically",
          "Eliminating the need for breeding programmes"
        ],
        "a": 1,
        "e": "Understanding evolutionary relationships helps conservationists prioritise which species and genetic diversity are most important to protect.",
        "h": "Recall the first conservation benefit listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Evolutionary understanding can help predict how species may respond to:",
        "o": [
          "Environmental change, such as climate change",
          "Only changes in the moon's phases",
          "Nothing at all, since evolution has no predictive value",
          "Only man-made buildings"
        ],
        "a": 0,
        "e": "Evolutionary understanding helps scientists predict how species may respond and potentially adapt to environmental changes, such as climate change.",
        "h": "Recall the specific example of environmental change mentioned.",
        "yr": "SS3"
      },
      {
        "q": "Breeding programmes for endangered species aim to maintain:",
        "o": [
          "Zero genetic diversity within the population",
          "Healthy genetic diversity within the population",
          "Only a single genetically identical individual",
          "The extinction of the species"
        ],
        "a": 1,
        "e": "Breeding programmes for endangered species aim to maintain healthy genetic diversity, which improves the population's long-term survival chances.",
        "h": "Think about why genetic diversity matters for a small population's health.",
        "yr": "SS3"
      },
      {
        "q": "Which best distinguishes 'selective breeding' from 'genetic engineering'?",
        "o": [
          "Selective breeding chooses existing organisms with desired traits to breed; genetic engineering directly modifies DNA",
          "They are exactly the same process",
          "Genetic engineering never involves DNA",
          "Selective breeding always involves laboratory DNA editing"
        ],
        "a": 0,
        "e": "Selective breeding involves choosing organisms with existing desirable traits to breed, while genetic engineering directly modifies an organism's DNA in the laboratory.",
        "h": "Recall the exam tip distinguishing these two applications.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an application of genetics specifically in agriculture, not medicine?",
        "o": [
          "Genetic counselling",
          "Hybridization to combine desirable crop traits",
          "Blood transfusion compatibility",
          "IVF"
        ],
        "a": 1,
        "e": "Hybridization to combine desirable crop traits is an agricultural application of genetics, unlike genetic counselling, blood transfusion, and IVF, which are medical applications.",
        "h": "Look for the option specifically related to crop or livestock breeding.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a medical, not agricultural, application of genetics?",
        "o": [
          "Selective breeding of high-yield crops",
          "Hybridization of plant varieties",
          "Genetic counselling for inherited disorders",
          "GMO pest-resistant maize"
        ],
        "a": 2,
        "e": "Genetic counselling for inherited disorders is a medical application of genetics, unlike the other listed options which relate to agriculture.",
        "h": "Look for the option related to human health rather than farming.",
        "yr": "SS3"
      },
      {
        "q": "Why is understanding sickle cell genetics particularly important in regions where malaria is common?",
        "o": [
          "Because it has no relevance to malaria at all",
          "Because the sickle cell allele's link to malaria resistance informs screening and counselling in such regions",
          "Because sickle cell disease cures malaria completely",
          "Because malaria only affects people without any genetic traits"
        ],
        "a": 1,
        "e": "In malaria-endemic regions, understanding the sickle cell allele's link to malaria resistance is particularly important for effective screening and genetic counselling programmes.",
        "h": "Think about the practical relevance of this genetic link in malaria-prone areas.",
        "yr": "SS3"
      },
      {
        "q": "A farmer crossing a high-yield maize variety with a disease-resistant maize variety to combine both traits is practising:",
        "o": [
          "Cloning",
          "Hybridization",
          "IVF",
          "Genetic counselling"
        ],
        "a": 1,
        "e": "Crossing two different varieties to combine desirable traits, such as high yield and disease resistance, is an example of hybridization.",
        "h": "Recall the definition of hybridization given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which application would most directly help a couple struggling with infertility to conceive?",
        "o": [
          "Selective breeding",
          "IVF (In Vitro Fertilization)",
          "Hybridization",
          "GMOs"
        ],
        "a": 1,
        "e": "IVF is specifically a medical technique designed to help couples facing fertility challenges conceive by enabling fertilization outside the body.",
        "h": "Recall which application is specifically linked to fertility challenges.",
        "yr": "SS3"
      },
      {
        "q": "Overall, why are genetics and evolution considered practically important beyond pure theory?",
        "o": [
          "They have no real-world uses at all",
          "They directly inform advances in agriculture, medicine, and conservation that affect food, health, and biodiversity",
          "They are only relevant to academic research with no societal impact",
          "They apply only to extinct species"
        ],
        "a": 1,
        "e": "Genetics and evolution have significant practical importance, directly informing advances in agriculture, medicine, and conservation efforts that affect food production, healthcare, and biodiversity.",
        "h": "Recall the introduction's framing of this topic's importance.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Ethical, Social and Economic Implications of Genetics and Evolution",
    "topicCode": "SS3-BIO-08",
    "module": "Applications and Implications",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Advances in genetics — cloning, stem cell research, and GMOs — raise important <span class=\"learn-keyword\">ethical, social, and economic questions</span> that societies, including Nigeria, must grapple with alongside the scientific possibilities.\n</div>\n\n<h3 class=\"learn-subheading\">1. Ethical Debates on Cloning</h3>\n<p class=\"learn-p\">Cloning raises questions about the moral status of cloned organisms, potential misuse (such as human reproductive cloning), and whether creating genetically identical copies of living things is ethically acceptable, particularly for humans.</p>\n\n<h3 class=\"learn-subheading\">2. Ethical Debates on Stem Cell Research</h3>\n<p class=\"learn-p\"><span class=\"learn-keyword\">Stem cells</span> are unspecialised cells capable of developing into different cell types, offering potential treatments for many diseases. However, when derived from human embryos, this research raises ethical concerns about the moral status of the embryo.</p>\n\n<h3 class=\"learn-subheading\">3. Advantages and Disadvantages of GMOs</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Advantages</th><th>Disadvantages</th></tr></thead>\n    <tbody>\n      <tr><td>Higher crop yields and pest resistance</td><td>Potential unknown long-term health effects</td></tr>\n      <tr><td>Improved nutritional content in some crops</td><td>Risk of reduced biodiversity if GMO crops dominate</td></tr>\n      <tr><td>Reduced need for chemical pesticides</td><td>Economic dependency on seed-producing companies</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Importance of Conserving Genetic Resources</h3>\n<p class=\"learn-p\">Preserving a wide range of genetic diversity — in crops, livestock, and wild species — is important because it provides a reservoir of traits (such as disease resistance or drought tolerance) that may be needed for future breeding programmes or to cope with environmental change.</p>\n\n<h3 class=\"learn-subheading\">5. Relevance to Nigeria's Development</h3>\n<p class=\"learn-p\">For Nigeria, responsible application of genetics — improved crop varieties suited to local conditions, genetic screening for inherited conditions like sickle cell disease, and conservation of indigenous plant and animal genetic resources — can support food security, public health, and sustainable development, provided ethical and regulatory safeguards are in place.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> When discussing GMOs or cloning in an essay or short answer, present a balanced view — mention both benefits and potential risks/concerns, rather than a one-sided argument.\n</div>\n",
    "questions": [
      {
        "q": "One major ethical concern raised by cloning is:",
        "o": [
          "Whether creating genetically identical copies of living things, particularly humans, is morally acceptable",
          "That cloning has no ethical implications whatsoever",
          "That cloning is scientifically impossible",
          "That cloning only concerns plants, never animals"
        ],
        "a": 0,
        "e": "Cloning raises significant ethical concerns, particularly around whether it is morally acceptable to create genetically identical copies of living things, especially humans.",
        "h": "Recall the ethical concern explicitly discussed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Stem cells are best described as:",
        "o": [
          "Fully specialised cells that cannot change",
          "Unspecialised cells capable of developing into different cell types",
          "Cells found only in fully grown adults",
          "A type of bacteria"
        ],
        "a": 1,
        "e": "Stem cells are unspecialised cells with the potential to develop into various different specialised cell types, offering promise for disease treatment.",
        "h": "Recall the definition of stem cells given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Stem cell research derived from human embryos raises ethical concerns primarily about:",
        "o": [
          "The moral status of the embryo",
          "The cost of laboratory equipment only",
          "Weather patterns",
          "The taste of stem cells"
        ],
        "a": 0,
        "e": "When stem cells are derived from human embryos, the research raises ethical concerns specifically about the moral status of the embryo.",
        "h": "Recall the specific ethical issue raised regarding embryonic stem cells.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is listed as an ADVANTAGE of GMOs?",
        "o": [
          "Potential unknown long-term health effects",
          "Higher crop yields and pest resistance",
          "Risk of reduced biodiversity",
          "Economic dependency on seed-producing companies"
        ],
        "a": 1,
        "e": "Higher crop yields and pest resistance are listed as key advantages of GMOs, improving agricultural productivity.",
        "h": "Look for the option in the 'Advantages' column of the table.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is listed as a DISADVANTAGE of GMOs?",
        "o": [
          "Improved nutritional content in some crops",
          "Reduced need for chemical pesticides",
          "Risk of reduced biodiversity if GMO crops dominate",
          "Higher crop yields"
        ],
        "a": 2,
        "e": "A risk of reduced biodiversity, if GMO crops come to dominate agriculture, is listed as a disadvantage in the lesson.",
        "h": "Look for the option in the 'Disadvantages' column of the table.",
        "yr": "SS3"
      },
      {
        "q": "Economic dependency on seed-producing companies is a concern related to:",
        "o": [
          "Stem cell research only",
          "GMOs",
          "Human evolution",
          "Natural selection"
        ],
        "a": 1,
        "e": "Economic dependency on seed-producing companies is listed as a disadvantage specifically related to the widespread adoption of GMOs.",
        "h": "Recall the economic disadvantage listed for GMOs.",
        "yr": "SS3"
      },
      {
        "q": "Conserving genetic resources in crops and livestock is important mainly because it:",
        "o": [
          "Has no practical benefit at all",
          "Provides a reservoir of traits, such as disease resistance, that may be needed for future breeding or environmental change",
          "Guarantees that no diseases will ever affect crops again",
          "Eliminates the need for any further scientific research"
        ],
        "a": 1,
        "e": "Conserving genetic resources provides a valuable reservoir of traits, such as disease resistance or drought tolerance, that may be needed for future breeding programmes or adapting to environmental change.",
        "h": "Recall the reasoning given for conserving genetic diversity.",
        "yr": "SS3"
      },
      {
        "q": "For Nigeria's development, genetic screening for conditions like sickle cell disease can support:",
        "o": [
          "Public health outcomes through early identification and counselling",
          "No meaningful benefit at all",
          "Only economic growth, with no health benefit",
          "The elimination of all agriculture"
        ],
        "a": 0,
        "e": "Genetic screening for conditions like sickle cell disease can support public health outcomes in Nigeria through early identification and informed counselling.",
        "h": "Recall the health-related benefit discussed for Nigeria specifically.",
        "yr": "SS3"
      },
      {
        "q": "Improved crop varieties suited to local Nigerian conditions can support:",
        "o": [
          "Food security",
          "The complete elimination of farming in Nigeria",
          "Only export markets, with no local benefit",
          "No agricultural improvement at all"
        ],
        "a": 0,
        "e": "Improved, locally-suited crop varieties, developed through genetics, can support food security as part of Nigeria's development.",
        "h": "Recall the agricultural development benefit discussed for Nigeria.",
        "yr": "SS3"
      },
      {
        "q": "According to the lesson, responsible application of genetics in Nigeria requires:",
        "o": [
          "No regulation or oversight at all",
          "Ethical and regulatory safeguards to be in place",
          "Complete avoidance of all genetic technologies",
          "Only foreign companies to manage all genetic research"
        ],
        "a": 1,
        "e": "The lesson emphasises that responsible application of genetics for Nigeria's development should be accompanied by ethical and regulatory safeguards.",
        "h": "Recall the concluding condition mentioned for responsible genetic application.",
        "yr": "SS3"
      },
      {
        "q": "When discussing GMOs in an essay, the exam tip advises candidates to:",
        "o": [
          "Present only the advantages, ignoring any risks",
          "Present only the disadvantages, ignoring any benefits",
          "Present a balanced view, mentioning both benefits and potential risks",
          "Avoid discussing GMOs altogether"
        ],
        "a": 2,
        "e": "The exam tip advises presenting a balanced view of GMOs, mentioning both benefits and potential risks or concerns, rather than a one-sided argument.",
        "h": "Recall the exam tip given at the end of the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Reduced need for chemical pesticides, as a result of pest-resistant GMO crops, is best classified as:",
        "o": [
          "An advantage of GMOs",
          "A disadvantage of GMOs",
          "Unrelated to GMOs entirely",
          "A form of cloning"
        ],
        "a": 0,
        "e": "Reduced need for chemical pesticides, due to pest-resistant GMO crops, is listed as an advantage in the lesson's comparison table.",
        "h": "Check which column this benefit falls under in the GMO table.",
        "yr": "SS3"
      },
      {
        "q": "Potential unknown long-term health effects of consuming GMO foods is classified as:",
        "o": [
          "An advantage of GMOs",
          "A disadvantage of GMOs",
          "A benefit of cloning",
          "Unrelated to genetics"
        ],
        "a": 1,
        "e": "Potential unknown long-term health effects are listed as a disadvantage or concern associated with GMOs in the lesson.",
        "h": "Check which column this concern falls under in the GMO table.",
        "yr": "SS3"
      },
      {
        "q": "Why might reduced biodiversity be a concern if GMO crops come to dominate agriculture?",
        "o": [
          "Because a smaller range of genetic diversity could make crops more vulnerable to new diseases or environmental changes",
          "Because biodiversity has no connection to agricultural resilience",
          "Because GMO crops always increase biodiversity automatically",
          "There is no connection between GMOs and biodiversity"
        ],
        "a": 0,
        "e": "If GMO crops dominate and reduce overall crop biodiversity, this could make agriculture more vulnerable to new diseases or environmental changes, since there would be less genetic variety to draw on.",
        "h": "Connect this concern to the earlier lesson on the importance of genetic diversity.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best represents a balanced ethical view on stem cell research, as encouraged by the lesson?",
        "o": [
          "Stem cell research offers real medical promise, but when embryos are involved, it raises genuine ethical questions worth considering",
          "Stem cell research has no possible medical benefit",
          "Stem cell research raises no ethical questions at all",
          "Stem cell research should never be discussed"
        ],
        "a": 0,
        "e": "A balanced view acknowledges both the genuine medical promise of stem cell research and the real ethical questions it raises when human embryos are involved.",
        "h": "Look for the option that presents both sides fairly.",
        "yr": "SS3"
      },
      {
        "q": "Human reproductive cloning is specifically mentioned in the lesson as an example of:",
        "o": [
          "A universally accepted, uncontroversial practice",
          "A potential misuse of cloning technology that raises ethical concern",
          "A form of genetic counselling",
          "A purely agricultural application"
        ],
        "a": 1,
        "e": "Human reproductive cloning is specifically mentioned as an example of a potential misuse of cloning technology that raises significant ethical concern.",
        "h": "Recall the specific example of cloning misuse given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Conservation of indigenous plant and animal genetic resources in Nigeria is presented as supporting:",
        "o": [
          "Sustainable development",
          "The complete elimination of indigenous species",
          "Only foreign agricultural interests",
          "No meaningful national benefit"
        ],
        "a": 0,
        "e": "Conservation of Nigeria's indigenous plant and animal genetic resources is presented as supporting sustainable development for the country.",
        "h": "Recall the development goal linked to genetic resource conservation.",
        "yr": "SS3"
      },
      {
        "q": "Which of these questions best represents an ethical (rather than purely scientific) question about genetics?",
        "o": [
          "'How many chromosomes does a human cell contain?'",
          "'Is it morally acceptable to clone a human being?'",
          "'What is the structure of a DNA molecule?'",
          "'How does meiosis produce gametes?'"
        ],
        "a": 1,
        "e": "'Is it morally acceptable to clone a human being?' is an ethical question about values and morality, distinct from purely factual/scientific questions about biological processes.",
        "h": "Look for the question concerned with morality, not biological mechanism.",
        "yr": "SS3"
      },
      {
        "q": "Overall, this topic emphasises that scientific advances in genetics and evolution should be considered alongside:",
        "o": [
          "No other considerations beyond pure scientific capability",
          "Ethical, social, and economic implications for society",
          "Only economic profit, with no ethical consideration",
          "Entertainment value only"
        ],
        "a": 1,
        "e": "The overall theme of this topic is that scientific advances in genetics and evolution should be considered alongside their ethical, social, and economic implications for society.",
        "h": "Recall the overarching framing given in the topic's introduction.",
        "yr": "SS3"
      },
      {
        "q": "Which best summarises the relevance of this topic to Nigeria specifically, according to the lesson?",
        "o": [
          "Genetics has no specific relevance to Nigeria",
          "Responsible genetic applications can support Nigeria's food security, public health, and sustainable development, with proper safeguards",
          "Nigeria should avoid all genetic research permanently",
          "Only wealthy countries can benefit from genetics"
        ],
        "a": 1,
        "e": "The lesson concludes that responsible genetic applications — in agriculture, health screening, and conservation — can support Nigeria's food security, public health, and sustainable development, provided ethical and regulatory safeguards are maintained.",
        "h": "Recall the concluding section specifically addressing Nigeria's development.",
        "yr": "SS3"
      }
    ]
  }
];
